import { Link } from 'react-router-dom';

const LAST_UPDATED = 'July 20, 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-2xl font-semibold text-dark-slate mb-3">{title}</h2>
    <div className="space-y-3 text-slate-gray leading-relaxed">{children}</div>
  </section>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-frost-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-dark-slate">GradLink</Link>
          <nav className="flex gap-6 text-sm text-slate-gray">
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
            <Link to="/terms" className="hover:text-dark-slate">Terms</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold text-dark-slate mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-gray mb-10">Last updated: {LAST_UPDATED}</p>

        <p className="text-slate-gray leading-relaxed mb-10">
          This Privacy Policy describes how GradLink ("GradLink," "we," "us," or "our")
          collects, uses, and shares information about you when you use the GradLink
          website, mobile application, and related services (collectively, the
          "Service"). By using the Service you agree to this Policy. If you do not
          agree, do not use the Service.
        </p>

        <Section id="who-we-are" title="1. Who we are">
          <p>
            GradLink is an alumni network platform for graduates of Historically Black
            Colleges and Universities (HBCUs) and other institutions. GradLink is
            operated in the United States. You can reach us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .
          </p>
        </Section>

        <Section id="information-we-collect" title="2. Information we collect">
          <p><strong>Account information you give us.</strong> When you create an
          account we collect the email address, name, and password (or Google account
          identity) you sign up with. If you use "Sign in with Google," Google provides
          us your email address, display name, profile photo, and Google account ID.</p>

          <p><strong>Profile information you choose to share.</strong> You may add:
          institution and graduation year, major, current title and company, industry,
          hometown, current location (self-reported free text), Greek-life affiliation,
          LinkedIn URL, personal website, phone number, biography, interests, work
          history, gender, profile photo, cover photo, and a short video introduction.
          These fields are optional and can be edited from your Settings at any time.</p>

          <p><strong>Content you create.</strong> We store the content you post to the
          Service, including feed posts, "clips" (short-form ephemeral posts that expire
          after 48 hours), direct messages and attachments, job posts, event listings,
          housing and marketplace listings, dating profiles and matches, mentorship
          requests, reviews, blog posts, comments, and service provider profiles.</p>

          <p><strong>Media you upload.</strong> Images and video files you upload
          (profile photos, post attachments, clips, message attachments, listing
          photos, resumes, etc.) are stored in Firebase Storage. Some media URLs are
          publicly reachable by anyone who has the link so they can be embedded in the
          product; do not upload media you do not want others to be able to view.</p>

          <p><strong>Communications and calls.</strong> Direct messages between users
          are stored on our servers. Video calls made through GradLink use browser
          peer-to-peer WebRTC and are not recorded or stored by us; we relay only the
          call-setup signaling messages.</p>

          <p><strong>Payment information.</strong> Payments and subscriptions are
          processed by Stripe on their own hosted checkout pages. We do not receive or
          store your full card number, CVC, or expiration date. We do receive and store
          transaction metadata such as the Stripe session ID, amount, currency, product
          or plan purchased, and payment status.</p>

          <p><strong>Support conversations.</strong> When you open the in-app support
          chat, we share your name, email, institution, GradLink user ID, and role
          with our support-chat provider (Crisp) so we can help you.</p>

          <p><strong>Push notifications.</strong> If you enable push notifications, your
          browser or device provides us with a push subscription endpoint and
          encryption keys, which we store so we can deliver notifications to you.</p>

          <p><strong>Usage analytics.</strong> We record basic page-view events (URL
          path, session identifier, time on page, and if you are signed in, your user
          ID) to understand how the Service is used and improve it. We do not currently
          use third-party analytics providers such as Google Analytics.</p>

          <p><strong>Technical information.</strong> Like most web services we
          automatically receive your IP address and browser information when you
          connect. We use IP addresses for security and rate-limiting purposes.</p>

          <p><strong>Information we do not collect.</strong> GradLink does not use
          browser or device geolocation, does not use IP-based geolocation for content
          personalization, and does not sell your data to advertisers.</p>
        </Section>

        <Section id="how-we-use" title="3. How we use your information">
          <ul className="list-disc list-outside pl-6 space-y-2">
            <li>To operate, maintain, and provide the features of the Service.</li>
            <li>To let you connect with other alumni, mentors, service providers,
              and recruiters.</li>
            <li>To personalize your feed, matches, and recommendations.</li>
            <li>To send you transactional email (welcome messages, verification,
              password resets, notifications you have subscribed to).</li>
            <li>To process payments and manage subscriptions.</li>
            <li>To moderate content and detect abuse (see Section 5).</li>
            <li>To respond to support requests.</li>
            <li>To keep the Service secure, prevent fraud, and enforce our Terms.</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </Section>

        <Section id="how-we-share" title="4. How we share your information">
          <p><strong>With other users.</strong> Information you add to your public
          profile, posts you make, and listings you create are visible to other
          GradLink users. You can control some visibility from Settings &rarr; Privacy
          (profile visibility, email visibility, location sharing).</p>

          <p><strong>With service providers we rely on.</strong> The Service is built
          on top of the following third-party processors, which handle data on our
          behalf under their own privacy terms:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li><strong>Google Firebase</strong> — authentication (including Google
              Sign-In) and media storage.</li>
            <li><strong>MongoDB Atlas</strong> — primary database for profile and
              content data.</li>
            <li><strong>Stripe</strong> — payment processing.</li>
            <li><strong>Resend</strong> — outbound transactional email delivery.</li>
            <li><strong>Anthropic (Claude)</strong> — automated content moderation,
              mentor matching, and optional AI resume-review features (see Section 5).</li>
            <li><strong>Crisp</strong> — in-app support chat.</li>
            <li><strong>Google</strong> — public STUN servers for WebRTC video calls;
              Google Calendar API if you choose to sync events.</li>
            <li><strong>Browser push providers</strong> (Google, Mozilla, Apple) — to
              deliver push notifications to your device.</li>
          </ul>

          <p><strong>For legal reasons.</strong> We may disclose information if we
          believe in good faith that it is necessary to comply with a law, regulation,
          legal process, or governmental request, or to protect the rights, property,
          or safety of GradLink, our users, or the public.</p>

          <p><strong>Business transfers.</strong> If GradLink is involved in a merger,
          acquisition, financing, or sale of assets, your information may be
          transferred as part of that transaction. We will notify you before your
          information becomes subject to a different privacy policy.</p>

          <p><strong>We do not sell your personal information.</strong></p>
        </Section>

        <Section id="ai" title="5. Automated processing and AI">
          <p>GradLink uses Anthropic's Claude models to help operate the Service:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Posts, comments, and other user content may be sent to Claude for
              moderation review before being shown widely.</li>
            <li>Mentor-matching suggestions are generated by Claude using profile
              information you have chosen to share.</li>
            <li>If you use the optional AI resume-review, reformat, or generate
              features, the text of your resume is sent to Claude and returned as a
              suggestion for you to review.</li>
          </ul>
          <p>Anthropic processes this data on our behalf under its own privacy terms
          and, per Anthropic's API terms, does not use it to train its models.</p>
        </Section>

        <Section id="cookies" title="6. Cookies and local storage">
          <p>We use the following browser storage mechanisms:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li><strong>Authentication cookies</strong> (<code>access_token</code>,{' '}
              <code>refresh_token</code>) — HTTP-only cookies used to keep you signed
              in. Refresh tokens last up to 7 days.</li>
            <li><strong>Firebase session</strong> — stored in your browser's IndexedDB
              by the Firebase SDK to maintain your sign-in state.</li>
            <li><strong>Local storage</strong> — theme preferences and PWA install
              dismissal state.</li>
            <li><strong>Session storage</strong> — a random session ID used to group
              page-view events during a single browsing session.</li>
            <li><strong>Service worker cache</strong> — the GradLink Progressive Web
              App caches the app shell so it works offline.</li>
            <li><strong>Third-party cookies</strong> — Crisp (support chat) sets its
              own cookies to remember conversation state.</li>
          </ul>
        </Section>

        <Section id="retention" title="7. Data retention and deletion">
          <p>We keep your account information and content for as long as your account
          is active. Clips are automatically deleted 48 hours after they are posted.
          When you delete an account, we delete or de-identify your profile and
          content within a reasonable period, except where we are required to retain
          certain records (for example, payment records for tax and accounting
          purposes).</p>
          <p>You can permanently delete your account and its associated data from
          inside the app: go to <strong>Settings &rarr; Account &rarr; Danger Zone</strong>{' '}
          and tap <strong>Delete Account</strong>. Deletion is immediate. If you
          cannot sign in, email{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>{' '}
            from the email address on your account. Full details of what is
          deleted and what is retained are on our{' '}
            <Link to="/delete-account" className="text-dark-slate underline">
              account deletion page
            </Link>
            .</p>
        </Section>

        <Section id="security" title="8. Security">
          <p>We protect your information with industry-standard measures, including:
          bcrypt password hashing, HTTP-only authentication cookies, HTTPS/TLS in
          transit, and access controls on our data stores. No online service is
          perfectly secure, and we cannot guarantee absolute security. If you become
          aware of a security issue, please email{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .</p>
        </Section>

        <Section id="your-rights" title="9. Your rights and choices">
          <p>You can:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Access and update your profile information from Settings.</li>
            <li>Control profile visibility, email visibility, and location visibility
              from Settings &rarr; Privacy.</li>
            <li>Turn notification categories on or off from Settings &rarr;
              Notifications.</li>
            <li>Unsubscribe from marketing emails via the link in any such email.
              Transactional emails (password resets, security notices) cannot be
              turned off while your account is active.</li>
            <li>Request a copy of your data, or its deletion, by emailing{' '}
              <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
                hello@gradlink.co
              </a>
              .</li>
          </ul>
          <p>Residents of certain U.S. states (including California) and other
          jurisdictions may have additional rights under local law, including the
          right to know, correct, delete, and opt out of certain uses of their
          personal information. You can exercise these rights through the same email
          address above.</p>
        </Section>

        <Section id="minors" title="10. Children">
          <p>GradLink is intended for adults age 18 and older. We do not knowingly
          collect personal information from children under 13, and dating features
          are strictly for adults. If you believe a child has provided us with
          personal information, contact us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>{' '}
            and we will remove it.</p>
        </Section>

        <Section id="international" title="11. International users">
          <p>GradLink is operated from the United States. If you access the Service
          from outside the United States, you understand that your information will
          be transferred to, stored, and processed in the United States, which may
          have data-protection laws different from those in your country.</p>
        </Section>

        <Section id="changes" title="12. Changes to this policy">
          <p>We may update this Privacy Policy from time to time. When we make
          material changes we will update the "Last updated" date above and, where
          appropriate, notify you inside the app or by email. Your continued use of
          the Service after an update means you accept the updated policy.</p>
        </Section>

        <Section id="contact" title="13. Contact us">
          <p>Questions or requests about this policy or your information? Email us
          at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .</p>
        </Section>

        <footer className="border-t border-slate-200 pt-6 mt-16 text-sm text-slate-gray flex flex-wrap gap-4 justify-between">
          <span>&copy; {new Date().getFullYear()} GradLink</span>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-dark-slate">Terms of Service</Link>
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Privacy;
